import { AutosizeMode } from "./widgets/interfaces/model/enumerations/AutosizeMode";
import { CanvasMode } from "./widgets/interfaces/model/enumerations/CanvasMode";
import { FillTypes, GeometryTypes } from "./widgets/interfaces/model/enumerations/index";
import { ITextStyles } from "./widgets/interfaces/model/index";

/** Max dimensions for the shape. 10 inches in EMU. */
export const MAX_DIMENSION = 914400;

/** Minimum dimensions for the shape. */
export const MIN_DIMENSION = 1;

/**
 * Constant to represent additional dimension factor. This additional dimension is being
 * added to the shapes' SVG's width and height to avoid caterpillar like effect while
 * moving the shape. This value is also being added to adjust handlers, selection box and
 * resize handlers to adjust their position according to SVG's additional width and height.
 */
export const ADDITIONAL_DIMENSION_FACTOR = 4;

/**
 * Additional Width and Height constants required for selection box resize handler
 * to work on safari
 */

export const ADDITIONAL_WIDTH = 7;
export const ADDITIONAL_HEIGHT = 20;

/**
 * Canvas padding constant
 */
export const CANVAS_PADDING = 25;

/**
 * Canvas margin constant
 */
export const MARGIN_10 = 10;

/**
 * Constant to represent 100%
 */
export const PERCENTAGE_HUNDRED = 100;

/**
 * Constant to represent 0%
 */
export const PERCENTAGE_ZERO = 0;

/**
 * Constant to represet zero angle
 */
export const ANGLE_ZERO = 0;

/**
 * Constant: size of margin around images to prevent edges showing
 */
export const IMAGE_MARGIN = 3;

/**
 * Radius for circular handle. Could be angle rotation handle or adjust handles.
 */
export const CIRCULAR_HANDLE_RADIUS = 3;

/**
 * Curve point difference to a given one on a given path.
 */
export const CURVE_POINT_DIFF = 3;

/**
 * Right side menu constant size
 */
export const RIGHT_SIDE_MENU_WIDTH = 261;

/**
 * Stroke width for resize handles, rotation handle, adjust handles
 */
export const SELECTION_HANDLER_STROKE_WIDTH = 1.2;

/**
 * Default radius for control points
 */
export const DEFAULT_CONTROL_POINT_RADIUS = 3;

/**
 * Selection box and rotation handler line stroke width
 */
export const SELECTION_STROKE_WIDTH = 0.6;

/**
 * Selection box rotate handler y axis constant with respective with shape
 */
export const ROTATE_HANDLER_LOCATION = 15;

/**
 * Default delay in milliseconds, used in set timeout
 */
export const IE_DOUBLE_CLICK_DELAY = 200;

/**
 * Constant to represent shape minimum size
 */
export const SHAPE_MIN_SIZE = 10;

/** Default shape properties */
export const DEFAULT_SHAPE_PROPERTIES = {

	spPr: {
		/** Shape fill */
		fill: {
			color: "#e6e6fa",
			type: FillTypes.SOLID_FILL,
		},
		/** Shape */
		geometry: {
			presetShape: "rect",
		},
		/** Shape outline properties */
		ln: {
			W: 2,
			dash: "SOLID",
			fill: {
				color: "black",
			},
		},
		verticalAlign: "center",
	},
};

export const AUTOSIZE = {
	FIXED: "fixedSize",
	HEIGHT: "fixedHeight",
	WIDTH: "fixedWidth",
};

export const TEXT_ALIGN = {
	BOTTOM: "flex-end",
	CENTER: "center",
	LEFT: "left",
	RIGHT: "right",
	TOP: "flex-start",
};

/**
 * Text shape drag defaults
 */
export const TEXT_SHAPE_DRAG_DEFAULT = {
	isTableShape: false,
	isTextShape: true,
	spPr: {
		/** Shape fill */
		fill: {
			color: "#ffffff",
			type: FillTypes.SOLID_FILL,
		},
		/** Shape outline properties */
		ln: {
			W: 1,
			dash: "SOLID",
			fill: {
				color: "#0784B6",
			},
		},
	},
};

/**
 * Text shape defaults
 */
export const TEXT_SHAPE_DEFAULT = {

	/** Shape fill */
	spPr: {
		fill: {
			color: "none",
			type: FillTypes.SOLID_FILL,
		},
		/** Shape outline properties */
		ln: {
			dash: undefined,
			fill: {
				type: FillTypes.NO_FILL,
			},
		},
	},
};

/**
 * Snap to guides with 5 points difference
 */
export const GUIDE_SNAP_DIFF = 5;

/**
 * Froala format styles
 */
export const FROALA_FORMAT_STYLES = [
	"em",
	"s",
	"strong",
	"u",
];

// Key for accessing autosize
export const AUTOSIZE_KEY = "autosize";

// Key for canvas editor that holds content that is "In-Bounds"
export const CANVAS_EDITOR_ROOT = "#canvas-editor-root";

// Key for accessing color
export const COLOR_KEY = "color";

// Key for accessing paragraph
export const PARAGRAPH_KEY = "paragraph";

// Key for accessing font size
export const FONTSIZE_KEY = "font-size";

// Key for accessing font size
export const TEXTALIGN_KEY = "text-align";

// Key for accessing font size
export const VERTICALALIGN_KEY = "verticalAlign";

// Key for accessing line height
export const LINEHEIGHT_KEY = "line-height";

// placeholder disabled id for placeholder elements
export const PLACEHOLDER_DISABLED_SPAN = "csi-canvas-placeholder-disabled-span";

// placeholder label id for placeholder elements
export const PLACEHOLDER_LABEL_SPAN = "csi-canvas-placeholder-label-span";

// placeholder optional rich text div
export const PLACEHOLDER_RICH_TEXT_DIV = "csi-canvas-placeholder-rich-text-div";

// placeholder multiline regex
export const PLACEHOLDER_MULTILINE_REGEX = new RegExp("<br\s*/?>", "gi");

// placeholder separator id for placeholder elements
export const PLACEHOLDER_SEPARATOR_SPAN = "csi-canvas-placeholder-separator-span";

// placeholder value id for placeholder elements
export const PLACEHOLDER_VALUE_SPAN = "csi-canvas-placeholder-value-span";

/** 
 * Brief overview of Canvas DOM Structure from .svgCanvas --> #canvas-editor-root
 * .svgCanvas (Direct parent of Dojo 2+ Canvas)
 *     .c8_canvas_root (Dojo 2+ Canvas Root is here, not a typo, it is the same name as below)
 *         .c8_canvas_root (All 4 Canvas layers (Create/Page/Selection/Guide) are merged here)
 *             .c8_canvas_root_inner (Drag classes for dnd to Canvas are applied here)
 *                 #canvas-editor-root (Editor Root, e.g., where pageObjects lie)
 */
export const SVG_CANVAS = "svgCanvas";

/**
 * Default HTML tag styles used for text in froala editor
 */
export const TAG_STYLES: ITextStyles = {
	EM: "italic",
	H1: "paragraph",
	H2: "paragraph",
	H3: "paragraph",
	H4: "paragraph",
	OL: "ol",
	S: "strikethrough",
	STRONG: "strong",
	U: "underline",
	UL: "ul",
};

/**
 * Header to Font Size Map
 */
export const HEADING_TAG_FONT_MAP: any = {
	h1: "24px",
	h2: "20px",
	h3: "16px",
	h4: "14px",
	h5: "12px",
};

/**
 * Stroke color for curve control point.
 */
export const CURVE_CONTROL_POINT_STROKE = "blue";

/**
 * Stroke color for curve being editing.
 */
export const CURVE_EDIT_STROKE_COLOR = "lightskyblue";

/**
 * Stroke width for selected curve.
 */
export const CURVE_SELECTION_STROKE_WIDTH = 18;

/**
 * Fill color for image placeholders
 */
export const IMAGE_PLACEHOLDER_FILL_COLOR = "#cccccc";

/**
 * Grid value in case of snap to grid.
 */
export const GRID_INC = 10;

/**
 * Rotation grid angle used when shift key is pressed while rotating shape
 * Value of ROTATE_GRID_ANGLE: 15
 */
export const ROTATE_GRID_ANGLE = Math.PI / 12;

/**
 * Text Shape increase height offset on text overflow
 */
export const EXT_OFFSET = 7;

/**
 * Overflow values
 */
export const OVERFLOW = {
	AUTO: "auto",
	HIDDEN: "hidden",
	SCROLL: "scroll",
};

export const CURSOR_CSS_MAP = {
	c8_canvas_corner_nwse:"nwse-resize",
	c8_canvas_corner_nesw:"nesw-resize",
	c8_canvas_h_resize:"ew-resize",
	c8_canvas_v_resize:"ns-resize",
};

/**
 * Close curve snap offset
 */
export const CURVE_SNAP_OFFSET = 2;

/**
 * Default WIDTH and HEIGHT of text shape, used while creating shape on single click
 * Default width should be equivalent to 18 characters long using a 12px font size (about 140px)
 */
export const TEXT_SHAPE_DEFAULT_WIDTH = 140;
export const TEXT_SHAPE_DEFAULT_HEIGHT = 20;

/** Defaults text styles */
export const TEXT_STYLE_DEFAULTS = {
	autosize: AutosizeMode.FIXED_HEIGHT,
	color: "#000000",
	"font-family": "Arial",
	"font-size": "12px",
	"font-style": "normal",
	"font-weight": "400",
	paragraph: "N",
	"text-align": "left",
	verticalAlign: "center",
};

/** line dash mapping */
export const STROKE_DASH_MAPPING: any = {
	DASH: "6",
	DOT: "3,6",
	LARGE_DASH: "18,9",
	LARGE_DASH_DOT: "18,6,6,6",
	MEDIUM_DASH: "12,6",
	SMALL_DOT: "3",
	SOLID: "none",
};

/** Id for highlight span */
export const HIGHLIGHT_ID = "csiFroalaLinkHighlight";

/** Highlight color */
export const HIGHLIGHT_COLOR = "#d9d9d9";

/** Handle display color */
export const HANDLE_COLOR = "grey";

/** Image text */
export const IMAGE_RESOURCE = "Image";

/** Snapping handle color */
export const SNAPPING_HANDLE_COLOR = "red";

/** Angle range in radians within which one handle snaps to the other */
export const HANDLE_SNAPPING_WINDOW = Math.PI / 24;

/** Blue color */
export const BLUE_COLOR = "#0000ff";

/** Black color */
export const BLACK_COLOR = "#000000";

/** White color */
export const WHITE_COLOR = "#ffffff";

/** Selected curve point color */
export const SELECTED_CURVE_POINT_COLOR = BLUE_COLOR;

/** Max z index */
export const MAX_Z_INDEX = 301;

/** Batch action string constant */
export const BATCH_ACTION = "BATCHING_REDUCER.BATCH";

/** Default font family for c8-canvas text */
export const DEFAULT_FONT_FAMILY = "Arial";

/** Default font size for c8-canvas text */
export const DEFAULT_FONT_SIZE = 12;

/** Default color for c8-canvas text */
export const DEFAULT_COLOR = "rgb(0, 0, 0)";

/** Default text box padding */
export const DEFAULT_TEXTBOX_PADDING = 20;

/** Indent size */
export const INDENT_SIZE = 20;

/** Getting delta width when typing based on line height */
export const FONT_SIZE_LINE_HEIGHT_LOG_CONSTANT = 35;

/** When drawing Pen tool curves, handles closer to the point than this will be treated as disabled */
export const MIN_PEN_CURVE_RADIUS = 5;

/** Minimum distance for final character from edge of shape when indenting */
export const MIN_RIGHT_DISTANCE_FOR_INDENT = 40;

/** Key to access pixel text */
export const PIXELS = "px";

/** Key to access present section of state */
export const PRESENT = "present";

/** Key to access selection section of state */
export const SELECTION_STATE = "selection";

/** Key to access shapeDrawMetadata section of state */
export const SHAPE_DRAW_METADATA = "shapeDrawMetadata";

/** Key to access metadata section of shapeDrawMetadata state */
export const METADATA = "metadata";

/** Key to access table dimensions section of shapeDrawMetadata state */
export const TABLE_DIMENSIONS = "tableDimensions";

/** Key to access columns of shapeDrawMetadata state */
export const COLUMNS = "columns";

/** Key to access rows of shapeDrawMetadata state */
export const ROWS = "rows";

/** Key to indicate a temporary shape used to draw items before render */
export const TEMP_SHAPE = "tempShape";

/** Key to indicate a temporary selection overlay */
export const TEMP_SHAPE_OVERLAY = "tempShapeOverlay";

/** String of 0 */
export const ZERO_TEXT = "0";

/** Minimum width for image placeholder, to avoid description and
 * dimensions text displaying badly at narrow widths
 */
export const MINIMUM_IMAGE_PLACEHOLDER_WIDTH = 100;

/** Default width for image placeholder */
export const DEFAULT_IMAGE_PLACEHOLDER_WIDTH = 160;

/** Default height for image placeholder */
export const DEFAULT_IMAGE_PLACEHOLDER_HEIGHT = 160;

/** Default height for attribute placeholder */
export const ATTRIBUTE_PLACEHOLDER_DEFAULT_HEIGHT = 20;

/** Padding inside attribute placeholders */
export const PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES = 3.4;

/** Space between shapes in attribute placeholder */
export const SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES = 2;

/** Left offset for attribute button */
export const ATTRIBUTE_BUTTON_LEFT_OFFSET = 18;

/** Top offset for attribute button */
export const ATTRIBUTE_BUTTON_TOP_OFFSET = 15;

/** Delay for movement on canvas */
export const CANVAS_DELAY_MS = 200;

/** Canvas PX Precision */
export const CANVAS_PX_PRECISION = 1;

/** Movement threshold */
export const MOVEMENT_THRESHOLD_PX = 5;

/** Default canvas mode */
export const DEFAULT_CANVAS_MODE = CanvasMode.SELECT;

export const DEFAULT_PLACEHOLDER_SHAPE_PROPERTIES = {

	spPr: {
		/** autosize setting */
		autosize: AutosizeMode.FIXED_WIDTH,
		/** Shape fill */
		fill: {
			color: IMAGE_PLACEHOLDER_FILL_COLOR,
			type: FillTypes.SOLID_FILL,
		},
		/** Shape */
		geometry: {
			presetShape: "rect",
			type: GeometryTypes.PRESET,
		},
		/** Shape outline properties */
		ln: {
			W: 2,
			dash: "SOLID",
			fill: {
				color: BLACK_COLOR,
			},
		},
	},
};
